// ============================================
// COMPONENT - AnnouncementManager (Carrusel)
// ============================================

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdStar, MdReportProblem, MdRocketLaunch, MdLightbulb, MdArticle,
  MdClose, MdChevronLeft, MdChevronRight,
} from 'react-icons/md';
import { useChangelogActivas, useMarcarChangelogVisto, isExternalUrl } from '@almacenes/core';
import type { Changelog } from '@almacenes/core';

// ─── SISTEMA DE DISEÑO EMOCIONAL POR TIPO ─────────────────────────────────────
//
//  CAMPAÑA     → Ámbar/dorado · puntos · urgencia positiva de oferta exclusiva
//  ALERTA      → Rojo profundo · rayas adv. · urgencia seria, acción requerida
//  NOVEDAD     → Violeta/índigo · brillo radial · emoción de lanzamiento
//  INFORMACIÓN → Azul cielo · limpio · calma, confianza, utilidad
//  OTRO        → Pizarra oscura · editorial · lectura tranquila
//
// ─────────────────────────────────────────────────────────────────────────────
const TYPE_CONFIG: Record<string, {
  label:          string;
  icon:           React.ElementType;
  gradient:       string;     // fondo del header
  headerPattern?: string;     // patrón CSS superpuesto en el header
  bodyBg:         string;     // color de fondo del cuerpo
  accentBar:      string;     // color del borde lateral izquierdo
  accentWidth:    string;     // grosor del borde lateral
  pulse?:         boolean;    // animar el ícono (ALERTA)
  badge?:         string;     // chip adicional en el header
}> = {

  // ⭐ CAMPAÑA — Oferta, oportunidad, "no te pierdas esto"
  CAMPANA: {
    label:         'Campaña',
    icon:          MdStar,
    gradient:      'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)',
    // Patrón de puntos: sensación de confeti / celebración
    headerPattern: 'radial-gradient(circle, rgba(255,255,255,0.22) 1.5px, transparent 1.5px)',
    bodyBg:        '#FFFBEB',
    accentBar:     '#f59e0b',
    accentWidth:   '4px',
  },

  // 🚨 ALERTA — Urgencia, peligro controlado, "debes actuar ahora"
  ALERTA: {
    label:         'Alerta importante',
    icon:          MdReportProblem,
    gradient:      'linear-gradient(135deg, #ef4444 0%, #7f1d1d 100%)',
    // Rayas diagonales: cinta de advertencia industrial
    headerPattern: 'repeating-linear-gradient(-45deg, transparent, transparent 10px, rgba(0,0,0,0.09) 10px, rgba(0,0,0,0.09) 20px)',
    bodyBg:        '#FFF5F5',
    accentBar:     '#ef4444',
    accentWidth:   '5px',
    pulse:         true,
    badge:         '¡Acción requerida!',
  },

  // 🚀 NOVEDAD — Lanzamiento, innovación, emoción de algo nuevo
  NOVEDAD: {
    label:         '¡Novedad!',
    icon:          MdRocketLaunch,
    gradient:      'linear-gradient(135deg, #7c3aed 0%, #3730a3 100%)',
    // Brillo radial esquina: sensación de explosión/lanzamiento
    headerPattern: 'radial-gradient(ellipse at 85% 15%, rgba(255,255,255,0.28) 0%, transparent 55%)',
    bodyBg:        '#F5F3FF',
    accentBar:     '#7c3aed',
    accentWidth:   '4px',
    badge:         'NUEVO',
  },

  // 💡 INFORMACIÓN — Tip útil, calma, confianza, claridad
  INFORMACION: {
    label:         'Consejo útil',
    icon:          MdLightbulb,
    gradient:      'linear-gradient(135deg, #0ea5e9 0%, #075985 100%)',
    bodyBg:        '#F0F9FF',
    accentBar:     '#0ea5e9',
    accentWidth:   '3px',
  },

  // 📄 OTRO — Editorial, artículo, lectura neutra
  OTRO: {
    label:         'Artículo',
    icon:          MdArticle,
    gradient:      'linear-gradient(135deg, #475569 0%, #1e293b 100%)',
    bodyBg:        '#F8FAFC',
    accentBar:     '#64748b',
    accentWidth:   '3px',
  },
};

const getTypeConfig = (tipo: string) =>
  TYPE_CONFIG[tipo] ?? TYPE_CONFIG['OTRO'];

// ─── Componente ────────────────────────────────────────────────────────────────
export const AnnouncementManager = () => {
  const navigate                          = useNavigate();
  const [currentIndex, setCurrentIndex]   = useState(0);
  const [isOpen, setIsOpen]               = useState(true);
  const [animDir, setAnimDir]             = useState<'left' | 'right' | null>(null);
  const seenIdsRef                        = useRef<Set<number>>(new Set());

  const { data: announcements = [] }    = useChangelogActivas();
  const { mutate: marcarVisto }         = useMarcarChangelogVisto();

  // Marcar como visto al llegar a cada slide
  useEffect(() => {
    if (announcements.length === 0) return;
    const current = announcements[currentIndex];
    if (current && !seenIdsRef.current.has(current.id)) {
      seenIdsRef.current.add(current.id);
      marcarVisto(current.id);
    }
  }, [currentIndex, announcements, marcarVisto]);

  // ── Navegación ──────────────────────────────────────────────────────────────
  const goTo = (index: number) => {
    if (index === currentIndex) return;
    setAnimDir(index > currentIndex ? 'left' : 'right');
    setTimeout(() => {
      setCurrentIndex(index);
      setAnimDir(null);
    }, 160);
  };

  const goPrev = () => { if (currentIndex > 0) goTo(currentIndex - 1); };
  const goNext = () => { if (currentIndex < announcements.length - 1) goTo(currentIndex + 1); };
  const handleClose = () => setIsOpen(false);

  const handleCta = (announcement: Changelog) => {
    if (announcement.urlBoton) {
      if (announcement.esLinkExterno || isExternalUrl(announcement.urlBoton)) {
        window.open(announcement.urlBoton, '_blank', 'noopener,noreferrer');
      } else {
        navigate(announcement.urlBoton);
      }
    }
    if (!announcement.urlBoton || currentIndex === announcements.length - 1) {
      handleClose();
    } else {
      goNext();
    }
  };

  // ── Guards ──────────────────────────────────────────────────────────────────
  if (!isOpen || announcements.length === 0) return null;

  const current     = announcements[currentIndex];
  const typeConf    = getTypeConfig(current.tipoMensaje);
  const TypeIcon    = typeConf.icon;
  const hasMultiple = announcements.length > 1;
  const isFirst     = currentIndex === 0;
  const isLast      = currentIndex === announcements.length - 1;

  const esCritico = Boolean(current.esCritico);
  const canClose  = !esCritico;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm sm:p-6"
      onClick={canClose ? handleClose : undefined}
    >
      <div
        className="rounded-t-3xl sm:rounded-2xl shadow-2xl w-full sm:max-w-2xl relative overflow-hidden flex flex-col max-h-[96vh] sm:max-h-[90vh]"
        style={{ background: typeConf.bodyBg }}
        onClick={(e) => e.stopPropagation()}
      >

        {/* ── HEADER ──────────────────────────────────────────────────────────── */}
        <div
          className="relative flex items-center gap-4 px-6 py-5 sm:px-8 sm:py-6"
          style={{ background: typeConf.gradient }}
        >
          {/* Patrón de fondo superpuesto */}
          {typeConf.headerPattern && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: typeConf.headerPattern,
                backgroundSize:  '20px 20px',
              }}
            />
          )}

          {/* Ícono (con animación de pulso para ALERTA) */}
          <div className="relative z-10 flex-shrink-0">
            <TypeIcon
              size={40}
              color="#fff"
              className={typeConf.pulse ? 'animate-pulse' : ''}
            />
          </div>

          {/* Label + badge */}
          <div className="relative z-10 flex items-center gap-2 flex-1 min-w-0">
            <span className="text-lg sm:text-xl font-extrabold uppercase tracking-widest text-white leading-none">
              {typeConf.label}
            </span>
            {typeConf.badge && (
              <span className="text-[11px] font-bold uppercase tracking-wide text-white bg-white/20 rounded-full px-2.5 py-1 leading-none flex-shrink-0">
                {typeConf.badge}
              </span>
            )}
          </div>

          {/* Dots de navegación */}
          {hasMultiple && (
            <div className="relative z-10 flex items-center gap-2 flex-shrink-0">
              {announcements.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  aria-label={`Ir al mensaje ${i + 1}`}
                  className="rounded-full transition-all duration-300 focus:outline-none"
                  style={{
                    width:           i === currentIndex ? '22px' : '8px',
                    height:          '8px',
                    backgroundColor: i === currentIndex
                      ? 'rgba(255,255,255,1)'
                      : 'rgba(255,255,255,0.45)',
                  }}
                />
              ))}
            </div>
          )}

          {/* Botón cerrar (solo no-críticos) */}
          {canClose ? (
            <button
              onClick={handleClose}
              aria-label="Cerrar anuncio"
              className="relative z-10 ml-1 rounded-full p-1.5 transition-colors flex-shrink-0"
              style={{ background: 'rgba(0,0,0,0.25)', color: '#fff' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.45)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'rgba(0,0,0,0.25)')}
            >
              <MdClose size={20} />
            </button>
          ) : (
            <div className="ml-1 w-[30px] flex-shrink-0" />
          )}
        </div>

        {/* ── Imagen / Video ────────────────────────────────────────────────── */}
        <div
          className="transition-opacity duration-150"
          style={{ opacity: animDir ? 0 : 1 }}
        >
          {current.videoEmbedUrl ? (
            <div className="w-full aspect-video bg-gray-900">
              <iframe
                src={current.videoEmbedUrl}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : current.imagenUrl ? (
            <div className="w-full h-52 sm:h-72 relative">
              <img
                src={current.imagenUrl}
                alt={current.titulo}
                className="w-full h-full object-cover"
              />
              {/* Fade que mezcla con el bodyBg del tipo */}
              <div
                className="absolute bottom-0 left-0 right-0 h-16"
                style={{ background: `linear-gradient(to top, ${typeConf.bodyBg}, transparent)` }}
              />
            </div>
          ) : null}
        </div>

        {/* ── Cuerpo ────────────────────────────────────────────────────────── */}
        <div
          className="pt-6 pb-3 px-1 overflow-y-auto transition-opacity duration-150"
          style={{ opacity: animDir ? 0 : 1 }}
        >
          {/* Barra lateral de acento + contenido */}
          <div
            className="mx-6 pl-5 pr-2 sm:mx-8 sm:pl-6"
            style={{
              borderLeft: `${typeConf.accentWidth} solid ${typeConf.accentBar}`,
            }}
          >
            <h2 className="text-2xl sm:text-3xl font-bold text-[#11194F] mb-3 leading-snug">
              {current.titulo}
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed whitespace-pre-wrap">
              {current.descripcion}
            </p>
          </div>
        </div>

        {/* ── Footer: navegación + CTA ──────────────────────────────────────── */}
        <div className="px-6 sm:px-8 pt-5 pb-8 space-y-4">

          {/* Flechas prev / next */}
          {hasMultiple && (
            <div className="flex items-center justify-between">
              <button
                onClick={goPrev}
                disabled={isFirst}
                aria-label="Mensaje anterior"
                className="flex items-center gap-1 text-base font-medium text-gray-400 hover:text-gray-700 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
              >
                <MdChevronLeft size={22} /> Anterior
              </button>
              <span className="text-sm text-gray-400 tabular-nums">
                {currentIndex + 1} / {announcements.length}
              </span>
              <button
                onClick={goNext}
                disabled={isLast}
                aria-label="Siguiente mensaje"
                className="flex items-center gap-1 text-base font-medium text-gray-400 hover:text-gray-700 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
              >
                Siguiente <MdChevronRight size={22} />
              </button>
            </div>
          )}

          {/* CTA — usa el mismo gradiente que el header */}
          <button
            onClick={() => handleCta(current)}
            className="w-full font-bold py-4 px-6 rounded-xl text-lg text-white transition-transform hover:scale-[1.02] active:scale-[0.98] shadow-md"
            style={{ background: typeConf.gradient }}
          >
            {current.textoBoton || (esCritico ? 'Entendido' : 'Cerrar')}
          </button>
        </div>
      </div>
    </div>
  );
};